import { getData } from "../../api/api";
import { buildReadQueryString } from "../api/readQuery";
import { combineAndFilters, quoteRsqlValue } from "../rsql";
import { anomalyService } from "../../services/anomalyService";
import { customerService } from "../../services/customerService";
import type { AnomalyEvent } from "../../types/anomalyEvent";
import type { Customer } from "../../services/customerService";
import { mapAnomalyEventFormToPayload } from "./form";

export class ApplyToAllCustomersError extends Error {
  readonly created: number;
  readonly skipped: number;

  constructor(created: number, skipped: number, cause?: unknown) {
    super("Failed to create anomaly for all customers");
    this.name = "ApplyToAllCustomersError";
    this.created = created;
    this.skipped = skipped;
    if (cause !== undefined) {
      (this as { cause?: unknown }).cause = cause;
    }
  }
}

const PAGE_SIZE = 200;
const MAX_PAGES = 50;

type Paged<T> = {
  content?: T[];
  totalPages?: number;
};

async function readAllPages<T>(load: (page: number) => Promise<Paged<T>>): Promise<T[]> {
  const all: T[] = [];
  for (let page = 0; page < MAX_PAGES; page += 1) {
    const response = await load(page);
    const content = response.content ?? [];
    all.push(...content);
    const totalPages = typeof response.totalPages === "number" ? response.totalPages : undefined;
    if (content.length < PAGE_SIZE || (totalPages != null && page + 1 >= totalPages)) {
      break;
    }
  }
  return all;
}

function sameText(left?: string | null, right?: string | null): boolean {
  return (left ?? "").trim().toLowerCase() === (right ?? "").trim().toLowerCase();
}

export async function createAnomalyForAllCustomers(
  formJson: Record<string, string>,
): Promise<{ created: number; skipped: number }> {
  const category = formJson.category?.trim() ?? "";
  const subCategory = formJson.subCategory?.trim() ?? "";
  const activityPeriod = formJson.activityPeriod?.trim() ?? "";
  const thresholdType = formJson.thresholdType?.trim() ?? "";

  const customers = await readAllPages<Customer>((page) =>
    customerService.getCustomers(page, PAGE_SIZE, undefined, ["id"]),
  );
  const customerIds = customers.map((customer) => customer.id?.trim()).filter((id): id is string => Boolean(id));
  if (customerIds.length === 0) {
    return { created: 0, skipped: 0 };
  }

  const search = combineAndFilters(
    `category==${quoteRsqlValue(category)}`,
    `subCategory==${quoteRsqlValue(subCategory)}`,
    `activityPeriod==${quoteRsqlValue(activityPeriod)}`,
  );
  const existing = await readAllPages<AnomalyEvent>((page) =>
    getData(
      `/anomaly-event/read?${buildReadQueryString({
        page,
        size: PAGE_SIZE,
        sort: "createdDate,desc",
        search,
        fields: ["customerId", "category", "subCategory", "activityPeriod", "thresholdType"],
      })}`,
    ),
  );

  const coveredCustomerIds = new Set(
    existing
      .filter(
        (event) =>
          Boolean(event.customerId?.trim()) &&
          sameText(event.category, category) &&
          sameText(event.subCategory, subCategory) &&
          sameText(event.activityPeriod, activityPeriod) &&
          sameText(event.thresholdType, thresholdType),
      )
      .map((event) => event.customerId!.trim().toLowerCase()),
  );

  const basePayload = mapAnomalyEventFormToPayload(formJson);
  let created = 0;
  let skipped = 0;

  for (const customerId of customerIds) {
    if (coveredCustomerIds.has(customerId.toLowerCase())) {
      skipped += 1;
      continue;
    }
    try {
      await anomalyService.createAnomalyEvent({
        ...basePayload,
        customerId,
      });
    } catch (error) {
      throw new ApplyToAllCustomersError(created, skipped, error);
    }
    coveredCustomerIds.add(customerId.toLowerCase());
    created += 1;
  }

  return { created, skipped };
}
